import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Steps, Button, Card, Input, message, Modal, Spin, Icon, DatePicker, Tag } from 'antd';
import moment from 'moment';

import Layout from '~/components/UI/Layout/Layout';
import { ModuleHeader } from '~/components/ModuleHeader';
import { PERMISSIONS } from '~/constants/permissions.constants';

import PassoPublico from './PassoPublico';
import PassoConteudo from './PassoConteudo';
import PassoRevisao from './PassoRevisao';
import { criarCampanha, salvarCampanha, buscarCampanha, previewPublico, dispararCampanha, agendarCampanha, desagendarCampanha } from '../api';
import { formataDuracao } from '../helpers';

const Step = Steps.Step;

const VAZIA = {
  name: '',
  subject: '',
  from_name: '',
  template_slug: null,
  content: {},
  body_html: null,
  filters: { situacao: 'ativos' },
  source: 'empresa',
};

/**
 * Editor de campanha em três passos.
 *
 * A campanha é salva a cada avanço de passo — assim o usuário pode sair no meio
 * e voltar depois, e o e-mail de teste (que precisa do id) fica disponível já no
 * passo de revisão.
 */
const EditorCampanha = ({ match, history }) => {
  const id = match && match.params && match.params.id !== 'nova' ? match.params.id : null;

  const [campanha, setCampanha] = useState(VAZIA);
  const [passo, setPasso] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const [carregando, setCarregando] = useState(!!id);
  const [funil, setFunil] = useState(null);
  const [previsao, setPrevisao] = useState(0);
  const [emailTeste, setEmailTeste] = useState('');
  const [agendando, setAgendando] = useState(false);
  const [quando, setQuando] = useState(null);

  // Campanha criada nesta sessão de edição já está inteira na tela. Sem esta
  // marca, o `history.replace` do primeiro salvamento faz o id aparecer na
  // rota, o efeito de carga busca a campanha recém-criada (ainda vazia) e a
  // resposta apaga tudo o que foi digitado desde então.
  const criadaAqui = useRef(false);

  const user = useSelector((state) => state.user);
  const isAdmin = !!(user && user.id) && user.permissions_group_id === 1;
  const permissoes = (user && user.permissions) || [];
  const podeDisparar = isAdmin || permissoes.indexOf(PERMISSIONS.marketing.campaigns.send) !== -1;

  useEffect(() => {
    if (!id || criadaAqui.current) return;
    buscarCampanha(id)
      .then((dados) => {
        if (!dados) return;
        // Campanha já enviada não se edita: o relatório é o lugar dela.
        if (dados.status !== 'draft' && dados.status !== 'scheduled') {
          history.replace('/marketing/campanhas/' + id + '/relatorio');
          return;
        }
        setCampanha({ ...VAZIA, ...dados, filters: dados.filters || VAZIA.filters });
      })
      .catch(() => message.error('Não foi possível carregar a campanha.'))
      .finally(() => setCarregando(false));
  }, [id, history]);

  // O funil também é buscado aqui porque a revisão precisa dele, e o passo 1
  // pode nem ter sido revisitado nesta sessão.
  const atualizarFunil = useCallback((filtros, source) => {
    previewPublico(filtros, source).then((dados) => {
      if (!dados) return;
      setFunil(dados.funil);
      setPrevisao(dados.previsao_minutos);
    });
  }, []);

  useEffect(() => {
    if (passo === 2) atualizarFunil(campanha.filters, campanha.source);
  }, [passo, campanha.filters, campanha.source, atualizarFunil]);

  const salvar = (dados) => {
    const payload = dados || campanha;

    if (!payload.name) {
      message.warning('Dê um nome à campanha para poder salvar.');
      return Promise.reject();
    }

    setSalvando(true);
    const acao = payload.id
      ? salvarCampanha(payload.id, payload)
      : criarCampanha(payload);

    return acao
      .then((salva) => {
        if (salva) {
          // Só o que é do servidor volta para o estado. Espalhar a resposta
          // inteira por cima (`...salva`) apagava o que a pessoa digitou
          // enquanto a requisição estava no ar: o passo seguinte abre logo
          // após o salvamento, e o primeiro campo preenchido lá era engolido
          // pela resposta do salvamento anterior.
          setCampanha((atual) => ({
            ...atual,
            id: salva.id || atual.id,
            status: salva.status || atual.status,
            // updated_marker força a prévia a recarregar quando o conteúdo muda
            updated_marker: Date.now(),
          }));
          if (!payload.id && salva.id) {
            criadaAqui.current = true;
            history.replace('/marketing/campanhas/' + salva.id);
          }
        }
        return salva;
      })
      .finally(() => setSalvando(false));
  };

  const avancar = () => {
    if (passo === 1 && !campanha.subject) {
      message.warning('O e-mail precisa de um assunto.');
      return;
    }
    salvar().then(() => setPasso(passo + 1)).catch(() => {});
  };

  const confirmarAgendamento = () => {
    if (!quando) {
      message.warning('Escolha a data e a hora.');
      return;
    }
    // A hora vai como relógio de parede; o servidor compara em horário de
    // São Paulo, que é o fuso do sistema inteiro.
    salvar()
      .then(() => agendarCampanha(campanha.id, quando))
      .then((salva) => {
        setCampanha((atual) => ({ ...atual, status: 'scheduled', scheduled_at: salva && salva.scheduled_at }));
        setAgendando(false);
        message.success('Campanha agendada para ' + quando.format('DD/MM [às] HH:mm') + '.');
      })
      .catch(() => message.error('Não foi possível agendar.'));
  };

  const cancelarAgendamento = () => {
    desagendarCampanha(campanha.id)
      .then(() => {
        setCampanha((atual) => ({ ...atual, status: 'draft', scheduled_at: null }));
        message.success('Agendamento cancelado. A campanha voltou a ser rascunho.');
      })
      .catch(() => message.error('Não foi possível cancelar o agendamento.'));
  };

  const confirmarDisparo = () => {
    if (!funil || !funil.receberao) {
      message.warning('Nenhum destinatário elegível com os filtros atuais.');
      return;
    }

    Modal.confirm({
      title: 'Disparar para ' + funil.receberao + ' pessoas?',
      content: (
        <div>
          <p>O envio começa agora e leva cerca de {formataDuracao(previsao)}.</p>
          <p style={{ marginBottom: 0, color: '#8c8c8c' }}>
            Depois do disparo a campanha não pode mais ser editada.
          </p>
        </div>
      ),
      okText: 'Disparar agora',
      cancelText: 'Voltar',
      onOk: () => dispararCampanha(campanha.id)
        .then((r) => {
          message.success((r && r.enfileirados ? r.enfileirados : '') + ' e-mail(s) na fila de envio.');
          history.push('/marketing/campanhas/' + campanha.id + '/relatorio');
        })
        .catch(() => message.error('Não foi possível disparar a campanha.')),
    });
  };

  if (carregando) {
    return <Layout><div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div></Layout>;
  }

  return (
    <Layout>
      {/* Sem ações no cabeçalho: a migalha já leva de volta às campanhas, e o
          rascunho se salva no rodapé, junto das outras decisões do passo. */}
      <ModuleHeader
        breadcrumbs={['Marketing', 'Campanhas', id ? campanha.name : 'Nova']}
        title={campanha.name || 'Nova campanha'}
      />

      {/* O nome pertence ao primeiro passo: é o que se decide ao começar, e
          repeti-lo nos três passos só rouba espaço de quem está escrevendo o
          e-mail — além de duplicar o título da própria página. */}
      {passo === 0 && (
        <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
          <div style={{ fontSize: 12, color: '#595959', marginBottom: 5, fontWeight: 500 }}>
            Nome da campanha <span style={{ color: '#8c8c8c', fontWeight: 400 }}>— só você vê</span>
          </div>
          <Input
            placeholder="Ex.: Simples Nacional 2027"
            value={campanha.name}
            onChange={(e) => setCampanha({ ...campanha, name: e.target.value })}
            style={{ maxWidth: 460 }}
          />
        </Card>
      )}

      <Steps current={passo} size="small" style={{ marginBottom: 20 }} onChange={(p) => p < passo && setPasso(p)}>
        <Step title="Público" description="quem recebe" />
        <Step title="Conteúdo" description="o que é enviado" />
        <Step title="Revisão" description="testar e disparar" />
      </Steps>

      {passo === 0 && (
        <PassoPublico
          filtros={campanha.filters || {}}
          source={campanha.source}
          onChange={(filtros, source) => setCampanha({ ...campanha, filters: filtros, source })}
        />
      )}

      {passo === 1 && (
        <PassoConteudo campanha={campanha} onChange={setCampanha} />
      )}

      {passo === 2 && (
        <PassoRevisao
          campanha={campanha}
          funil={funil}
          previsao={previsao}
          emailTeste={emailTeste}
          onChangeEmailTeste={setEmailTeste}
        />
      )}

      {/* Barra flutuante: as ações acompanham a rolagem em vez de ficarem no fim
          de uma página longa. Era o defeito mais caro da revisão — o botão de
          disparo nascia abaixo da dobra, atrás da prévia inteira do e-mail. */}
      <div
        style={{
          position: 'sticky', bottom: 16, zIndex: 10, marginTop: 20,
          background: '#fff', border: '1px solid #e8e8e8', borderRadius: 12,
          boxShadow: '0 6px 24px rgba(15,20,30,.12)',
          padding: '12px 16px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button disabled={passo === 0} onClick={() => setPasso(passo - 1)}>
            <Icon type="left" /> Voltar
          </Button>

          {/* O número fica ao lado do botão que o consome: na hora de disparar,
              "para quantas pessoas" não pode estar a uma rolagem de distância. */}
          {passo === 2 && funil && (
            <span style={{ fontSize: 13, color: '#8c8c8c' }}>
              <strong style={{ color: '#237804', fontSize: 15 }}>{funil.receberao}</strong>
              {funil.receberao === 1 ? ' pessoa vai receber' : ' pessoas vão receber'}
            </span>
          )}
        </span>

        <span>
          <Button
            style={{ marginRight: 8 }}
            loading={salvando}
            onClick={() => salvar().then(() => message.success('Rascunho salvo!')).catch(() => {})}
          >
            Salvar rascunho
          </Button>

        {passo < 2 ? (
          <Button type="primary" onClick={avancar} loading={salvando}>
            Continuar <Icon type="right" />
          </Button>
        ) : podeDisparar ? (
          <span>
            {campanha.status === 'scheduled' ? (
              <span>
                <Tag color="blue" style={{ marginRight: 8 }}>
                  <Icon type="clock-circle" />{' '}
                  {campanha.scheduled_at
                    ? moment(campanha.scheduled_at).format('DD/MM [às] HH:mm')
                    : 'agendada'}
                </Tag>
                <Button style={{ marginRight: 8 }} onClick={cancelarAgendamento}>
                  Cancelar agendamento
                </Button>
              </span>
            ) : (
              <Button icon="clock-circle" style={{ marginRight: 8 }} onClick={() => setAgendando(true)}>
                Agendar
              </Button>
            )}

            <Button type="primary" icon="send" onClick={confirmarDisparo}>
              {funil && funil.receberao
                ? 'Disparar para ' + funil.receberao + ' pessoas'
                : 'Disparar'}
            </Button>
          </span>
        ) : (
          <Button disabled title="Você não tem permissão para disparar campanhas">
            Disparar
          </Button>
        )}
        </span>
      </div>

      <Modal
        title="Agendar o disparo"
        visible={agendando}
        onOk={confirmarAgendamento}
        onCancel={() => setAgendando(false)}
        okText="Agendar"
        cancelText="Voltar"
      >
        <div style={{ fontSize: 13, color: '#595959', marginBottom: 12 }}>
          A campanha sai sozinha na data escolhida. O sistema confere a cada cinco
          minutos, então a hora é aproximada nesse intervalo.
        </div>
        <DatePicker
          showTime={{ format: 'HH:mm' }}
          format="DD/MM/YYYY HH:mm"
          placeholder="Escolha data e hora"
          style={{ width: '100%' }}
          value={quando}
          onChange={setQuando}
          disabledDate={(d) => d && d < moment().startOf('day')}
        />
        {funil && (
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 10 }}>
            O público é recalculado na hora do disparo, não agora — quem entrar
            na base até lá também recebe.
          </div>
        )}
      </Modal>
    </Layout>
  );
};

export default EditorCampanha;
